export const REQUIRED_FIELD_ERROR_MESSAGE = 'This field is required.'

/**
 * A form field validator that checks if a value is required (non-empty).
 *
 * This validator is commonly used with TanStack Form or similar form libraries
 * to ensure required fields are filled out before form submission.
 *
 * @param props - The validator input object
 * @param props.value - The field value to validate
 * @returns `null` if the field is valid, or an error message string if invalid
 *
 * @example
 * ```ts
 * // Usage with TanStack Form
 * const field = useField({
 *   name: 'username',
 *   validators: { onChange: requiredFieldValidator }
 * });
 *
 * // Direct usage
 * const error = requiredFieldValidator({ value: '' }); // "This field is required."
 * const noError = requiredFieldValidator({ value: 'John' }); // null
 * ```
 */
export const requiredFieldValidator = ({ value }: { value: string }) => {
  return value.length === 0 ? REQUIRED_FIELD_ERROR_MESSAGE : null
}

export const domainListValidator = ({ value }: { value: string }) => {
  if (value.length === 0) {
    return REQUIRED_FIELD_ERROR_MESSAGE
  }

  // Split by comma and trim whitespace
  const domains = value
    .split(',')
    .map((domain) => domain.trim())
    .filter((domain) => domain.length > 0)

  if (domains.length === 0) {
    return 'At least one domain is required.'
  }

  // Domain validation: requires proper domain structure with at least one period separating labels
  // Accepts any non-whitespace, non-period characters in each label (very permissive for international domains)
  const domainRegex = /^[^\s.]+(\.[^\s.]+)+$/

  for (const domain of domains) {
    // Check if domain matches the pattern
    if (!domainRegex.test(domain)) {
      return `"${domain}" is not a valid domain name.`
    }
  }

  return null
}
